import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

interface PacInstructivoCardProps {
  customerCode: string | null;
  loading?: boolean;
}

const PAC_SERVICE_NAME = 'Junngla'; // TODO: reemplazar cuando se confirme nombre comercial definitivo.

export default function PacInstructivoCard({ customerCode, loading }: PacInstructivoCardProps) {
  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="instructivo" className="border-b-0">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-start gap-3 text-left">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-flux-primary/10 text-flux-primary">
                <span className="material-symbols-outlined">info</span>
              </span>
              <div className="flex-1">
                <h2 className="text-lg font-medium">Cómo activar tu mandato PAC</h2>
                <p className="mt-1 text-sm text-muted-foreground">Pasos para autorizar el cobro automático desde el portal de tu banco. Haz clic para ver el detalle.</p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground">
              Sigue estos pasos para autorizar a {PAC_SERVICE_NAME} a cobrar tus cuotas automáticamente desde tu cuenta bancaria. El proceso lo realizas directamente en el sitio web de tu banco; al
              finalizar, vuelve a esta pantalla para subir tu comprobante (opcional).
            </p>

            <ol className="mt-5 space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">1</span>
                <span>
                  <strong>Haz clic en "Activar mandato"</strong> junto a la cuenta que quieres autorizar. Se abrirá el sitio web de tu banco en una nueva pestaña.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">2</span>
                <span>
                  <strong>Inicia sesión</strong> en el portal del banco con tus credenciales habituales.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">3</span>
                <span>
                  <strong>Busca la sección de mandatos PAC</strong> (Pago Automático de Cuentas). Según el banco, puede aparecer como <em>"PAC"</em>, <em>"Pago Automático de Cuentas"</em>,{' '}
                  <em>"Inscripción PAC"</em> o <em>"Mandatos"</em>, normalmente dentro del menú de pagos o servicios.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">4</span>
                <span>
                  <strong>Crea un nuevo mandato PAC</strong> y selecciona <strong>"{PAC_SERVICE_NAME}"</strong> como empresa o servicio en el listado del banco.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">5</span>
                <span>
                  <strong>Ingresa tu código de cliente:</strong>{' '}
                  {loading ? (
                    <span className="inline-block h-5 w-32 animate-pulse rounded bg-slate-200 align-middle dark:bg-slate-700" />
                  ) : (
                    <code className="inline-block rounded bg-slate-100 px-2 py-0.5 font-mono text-sm font-semibold text-flux-primary dark:bg-slate-800">{customerCode ?? '—'}</code>
                  )}
                  <span className="ml-1 text-xs text-muted-foreground">(10 dígitos numéricos — cópialo o anótalo antes de continuar)</span>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-700">!</span>
                <span>
                  <strong>No establezcas un monto máximo.</strong> Deja el campo en blanco o marca la opción "sin tope" / "sin monto máximo". Esto permite que {PAC_SERVICE_NAME} cobre el valor exacto
                  de cada cuota.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">7</span>
                <span>
                  <strong>Confirma el mandato</strong> según el método de autenticación de tu banco (clave dinámica, Digipass, biometría, etc.).
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-flux-primary/10 text-xs font-semibold text-flux-primary">8</span>
                <span>
                  <strong>Vuelve a esta pantalla</strong> y, si tu banco te entregó un comprobante de activación, súbelo en el botón "Subir comprobante". Este paso es opcional pero nos ayuda a validar
                  tu activación más rápido.
                </span>
              </li>
            </ol>

            <div className="mt-5 space-y-2 rounded-lg bg-slate-50 p-4 text-xs text-muted-foreground dark:bg-slate-900/40">
              <p>
                <strong>¿Tienes problemas?</strong> Si tu banco no muestra "{PAC_SERVICE_NAME}" en el listado, espera unos minutos e intenta de nuevo, o contacta a soporte.
              </p>
              <p>
                <strong>Para dar de baja tu mandato</strong>, deberás hacerlo directamente desde el portal web de tu banco; no se gestiona desde {PAC_SERVICE_NAME}.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
